import { Model } from './model';
import type { CountryCode } from 'libphonenumber-js';

// `off` hard-pins to `language_code`. `flux` and `scribe` are legacy values kept
// for existing rows; the dropdown no longer offers them.
export type SttMode = 'auto' | 'flux' | 'off' | 'scribe';

export class RetellVoice extends Model {
  accent: string;
  age: string;
  gender: string;
  preview_audio_url: string;
  provider: string;
  voice_id: string;
  voice_name: string;
}

export class RetellAgent extends Model {
  agent_id: string;
  agent_name: string;
  interruption_sensitivity: number;
  language: string;
  responsiveness: number;
  voice_speed: number;
  voice_temperature: number;
  voice_id: string;
}

export class RetellWebCallAccessToken extends Model {
  access_token: string;
}

export type VoiceSpeed = 'slowest' | 'slow' | 'normal' | 'fast' | 'fastest';

export const STT_MODES = ['auto', 'flux', 'off', 'scribe'] as const;

// User-facing dropdown values. Legacy rows render as their modern equivalent:
// scribe → auto (same multilingual Scribe), flux → off + en-US (same Flux engine).
export const STT_MODE_OPTIONS = ['auto', 'off'] as const;
export type SelectableSttMode = (typeof STT_MODE_OPTIONS)[number];

export const STT_MODE_LABELS: Record<SelectableSttMode, string> = {
  auto: 'Auto Switch',
  off: 'Hardcoded',
};

export const STT_MODE_DESCRIPTIONS: Record<SelectableSttMode, string> = {
  auto: 'Start with the selected language; auto-detect and switch if the caller speaks a different language.',
  off: 'Hardcode the call to the selected language; never switch. English uses Deepgram Flux (best English-only quality).',
};

// --- staff-only model pickers (Stuut staff / is_stuut_user) ---
export const LLM_MODEL_LABELS: Record<string, string> = {
  'google/gemini-2.5-flash': 'Gemini 2.5 Flash',
  'google/gemini-2.5-flash-lite': 'Gemini 2.5 Flash Lite',
  'openai/gpt-4.1': 'GPT-4.1',
  'openai/gpt-4.1-mini': 'GPT-4.1 Mini',
};

export const LLM_MODEL_DESCRIPTIONS: Record<string, string> = {
  'google/gemini-2.5-flash': 'Default. Balanced latency and quality.',
  'google/gemini-2.5-flash-lite': 'Lowest latency.',
  'openai/gpt-4.1': 'OpenAI flagship.',
  'openai/gpt-4.1-mini': 'Faster, cheaper OpenAI.',
};

export const TTS_MODEL_LABELS: Record<string, string> = {
  eleven_flash_v2_5: 'Flash v2.5',
  eleven_flash_v2: 'Flash v2 (English)',
  eleven_multilingual_v2: 'Multilingual v2',
};

export const TTS_MODEL_DESCRIPTIONS: Record<string, string> = {
  eleven_flash_v2_5: 'Default. ~75ms latency, 32 languages.',
  eleven_flash_v2: 'English-only, ~75ms.',
  eleven_multilingual_v2: 'Higher quality, higher latency.',
};

// TTS model ids that can only speak English (values match TtsModel in the SDK). Selecting one
// pins the agent to hardcoded English; moving the language off English reverts TTS to default.
export const ENGLISH_ONLY_TTS_MODELS: ReadonlySet<string> = new Set(['eleven_flash_v2']);

// The "multilingual / auto-detect" sentinel language code — a SUPPORTED_LANGUAGES key and
// the value meaning "no specific starting language".
export const MULTI_LANGUAGE = 'multi';

// Default starting language when an agent is pinned/hardcoded to English (STT off, or an
// English-only TTS model).
export const DEFAULT_HARDCODED_LANGUAGE = 'en-US';

// Shared BCP-47 list for the call-agent and partner pickers. Keep in sync with
// the `Locale` literal in stuut-py `stuut.livekit.types`.
export const SUPPORTED_LANGUAGES: Record<string, string> = {
  multi: 'Multilingual (auto-detect)',
  'bg-BG': 'Bulgarian',
  'ca-ES': 'Catalan',
  'da-DK': 'Danish',
  'de-DE': 'German',
  'el-GR': 'Greek',
  'en-AU': 'English (Australia)',
  'en-GB': 'English (United Kingdom)',
  'en-IN': 'English (India)',
  'en-NZ': 'English (New Zealand)',
  'en-US': 'English (United States)',
  'es-419': 'Spanish (Latin America)',
  'es-ES': 'Spanish (Spain)',
  'et-EE': 'Estonian',
  'fi-FI': 'Finnish',
  'fr-CA': 'French (Canada)',
  'fr-FR': 'French (France)',
  'ga-IE': 'Irish',
  'hi-IN': 'Hindi',
  'hu-HU': 'Hungarian',
  'id-ID': 'Indonesian',
  'it-IT': 'Italian',
  'ja-JP': 'Japanese',
  'ko-KR': 'Korean',
  'lt-LT': 'Lithuanian',
  'lv-LV': 'Latvian',
  'nb-NO': 'Norwegian (Bokmål)',
  'nl-BE': 'Dutch (Belgium)',
  'nl-NL': 'Dutch (Netherlands)',
  'no-NO': 'Norwegian',
  'pl-PL': 'Polish',
  'pt-BR': 'Portuguese (Brazil)',
  'pt-PT': 'Portuguese (Portugal)',
  'ro-RO': 'Romanian',
  'ru-RU': 'Russian',
  'sk-SK': 'Slovak',
  'sv-SE': 'Swedish',
  'th-TH': 'Thai',
  'tr-TR': 'Turkish',
  'vi-VN': 'Vietnamese',
  'zh-CN': 'Chinese (Simplified)',
};

export class CallAgent extends Model {
  id: string;
  end_call_on_recording_refusal: boolean;
  recording_disclaimer_required: boolean;
  retell_agent: RetellAgent;
  outbound_begin_message: string;
  stt_mode: SttMode;
  // BCP-47 starting language. Observer hint on AUTO; hard pin on OFF (required).
  // Overridden per-call by partner.retell_language_code.
  language_code?: string | null;
  voice_id?: string | null;
  voice_speed?: VoiceSpeed;
  voice_temperature?: number;
  custom_prompt?: string | null;
  // Staff-only model overrides (null = registry default).
  llm_model?: string | null;
  tts_model?: string | null;
}

export interface Country {
  name: string;
  isoCode: CountryCode;
  callingCode: string;
}
